import React from 'react';
import styled, { keyframes } from 'styled-components';

const AIWebinarHero = () => {
  // Split "FUTURE" into individual letters for animation
  const futureText = "FUTURE";
  const futureLetters = futureText.split('');

  return (
    <HeroContainer id="hero">
      <OverlayGradient />
      <ContentWrapper>
        <WebinarBadge>FREE AI WEBINAR</WebinarBadge>
        <HeroHeading>
          <TransformTextContainer>
            {futureLetters.map((letter, index) => (
              <AnimatedLetter key={index} delay={index * 0.1}>
                {letter}
              </AnimatedLetter>
            ))}
          </TransformTextContainer>
          <span>-PROOF YOUR BUSINESS</span>
        </HeroHeading>
        <Subheading>
          <StrongText>AI-POWERED SOLUTIONS</StrongText> Built To Scale
        </Subheading>
        <HeroText>
          Learn how AI can transform your business operations, boost efficiency, and give you a competitive edge in today's market.
        </HeroText>
        <CTAContainer>
          <MainCTA href="#webinar-signup">
            RESERVE YOUR SPOT
            <CTAArrow>→</CTAArrow>
          </MainCTA>
        </CTAContainer>
        <TrustBadges>
          <Badge>
            <CheckIcon>✓</CheckIcon>
            <BadgeText>100% Free Webinar</BadgeText>
          </Badge>
          <Badge>
            <CheckIcon>✓</CheckIcon>
            <BadgeText>Live Q&A Session</BadgeText>
          </Badge>
          <Badge>
            <CheckIcon>✓</CheckIcon>
            <BadgeText>Actionable Strategies</BadgeText>
          </Badge>
        </TrustBadges>
      </ContentWrapper>
      <ImageContainer>
        <WebinarImage src="https://images.unsplash.com/photo-1639762681057-408e52192e55?q=80&w=2232&auto=format&fit=crop" alt="AI Technology Visualization" />
        <FloatingElements>
          <FloatingCircle delay="0s" top="20%" left="10%" size="40px" />
          <FloatingCircle delay="1s" top="70%" left="15%" size="25px" />
          <FloatingCircle delay="2s" top="30%" left="80%" size="35px" />
          <FloatingCircle delay="3s" top="80%" left="70%" size="30px" />
          <FloatingLine top="25%" left="30%" width="100px" rotate="30deg" delay="0.5s" />
          <FloatingLine top="65%" left="60%" width="80px" rotate="-15deg" delay="1.5s" />
          <FloatingLine top="40%" left="70%" width="120px" rotate="60deg" delay="2.5s" />
        </FloatingElements>
      </ImageContainer>
    </HeroContainer>
  );
};

// Animations
const jumpUp = keyframes`
  0%, 100% {
    transform: translateY(0) rotate(0deg);
    text-shadow: 0 5px 10px rgba(0, 255, 198, 0.3);
  }
  50% {
    transform: translateY(-20px) rotate(5deg);
    text-shadow: 0 20px 30px rgba(0, 255, 198, 0.5);
  }
`;

const pulse = keyframes`
  0% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(0, 255, 198, 0.7);
  }
  
  70% {
    transform: scale(1.05);
    box-shadow: 0 0 0 15px rgba(0, 255, 198, 0);
  }
  
  100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(0, 255, 198, 0);
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const float = keyframes`
  0% { transform: translateY(0) translateX(0); }
  50% { transform: translateY(-10px) translateX(5px); }
  100% { transform: translateY(0) translateX(0); }
`;

const glow = keyframes`
  0% { box-shadow: 0 0 5px 0 rgba(0, 255, 198, 0.5); }
  50% { box-shadow: 0 0 20px 5px rgba(0, 255, 198, 0.8); }
  100% { box-shadow: 0 0 5px 0 rgba(0, 255, 198, 0.5); }
`;

// Styled Components
const TransformTextContainer = styled.span`
  display: inline-flex;
  color: #00FFC6;
  margin-right: 10px;
`;

const AnimatedLetter = styled.span`
  display: inline-block;
  font-weight: 800;
  color: #00FFC6;
  text-shadow: 2px 2px 0 rgba(0, 0, 0, 0.3), 
               4px 4px 0 rgba(0, 255, 198, 0.4),
               6px 6px 10px rgba(0, 0, 0, 0.5);
  animation: ${jumpUp} 3s ease-in-out infinite;
  animation-delay: ${props => props.delay}s;
  animation-fill-mode: both;
  transform-origin: bottom center;
`;

const HeroContainer = styled.section`
  position: relative;
  display: flex;
  padding: 140px 80px 100px;
  background: linear-gradient(135deg, #0a0e17, #161b2b);
  min-height: 85vh;
  overflow: hidden;
  
  @media (max-width: 991px) {
    flex-direction: column;
    padding: 120px 40px 60px;
  }
  
  @media (max-width: 768px) {
    padding: 100px 20px 60px;
  }
`;

const OverlayGradient = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(circle at top right, rgba(0, 255, 198, 0.1) 0%, rgba(10, 14, 23, 0.9) 70%);
  z-index: 1;
`;

const ContentWrapper = styled.div`
  position: relative;
  z-index: 2;
  width: 55%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  animation: ${fadeIn} 0.8s ease-out;
  
  @media (max-width: 991px) {
    width: 100%;
    margin-bottom: 40px;
  }
`;

const WebinarBadge = styled.div`
  display: inline-block;
  background: linear-gradient(135deg, #00FFC6, #0057FF);
  color: #ffffff;
  font-weight: 700;
  padding: 10px 25px;
  border-radius: 30px;
  font-size: 1rem;
  margin-bottom: 30px;
  animation: ${pulse} 2s infinite;
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
    padding: 8px 20px;
  }
`;

const HeroHeading = styled.h1`
  font-family: 'Manrope', sans-serif;
  font-size: 4rem;
  font-weight: 800;
  line-height: 1.1;
  color: #ffffff;
  margin-bottom: 20px;
  letter-spacing: -0.5px;
  
  @media (max-width: 991px) {
    font-size: 3rem;
  }
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const Subheading = styled.h2`
  font-family: 'Manrope', sans-serif;
  font-size: 1.8rem;
  font-weight: 600;
  color: #cccccc;
  margin-bottom: 30px;
  line-height: 1.4;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const StrongText = styled.span`
  background: linear-gradient(135deg, #00FFC6, #0057FF);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  font-weight: 800;
`;

const HeroText = styled.p`
  font-size: 1.2rem;
  line-height: 1.6;
  color: #a0a0a0;
  margin-bottom: 40px;
  max-width: 90%;
  
  em {
    font-style: normal;
    color: #00FFC6;
    font-weight: 500;
  }
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
    margin-bottom: 30px;
  }
`;

const CTAContainer = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 40px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 15px;
  }
`;

const MainCTA = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #00FFC6, #0057FF);
  color: #000;
  font-weight: 700;
  font-size: 1rem;
  padding: 18px 35px;
  border-radius: 8px;
  text-decoration: none;
  transition: all 0.3s ease;
  border: none;
  cursor: pointer;
  box-shadow: 0 10px 25px rgba(0, 255, 198, 0.3);
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 30px rgba(0, 255, 198, 0.4);
  }
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
    padding: 15px 30px;
  }
`;

const CTAArrow = styled.span`
  margin-left: 8px;
  font-size: 1.2rem;
  font-weight: bold;
  transition: transform 0.3s ease;
  
  ${MainCTA}:hover & {
    transform: translateX(5px);
  }
`;

const TrustBadges = styled.div`
  display: flex;
  gap: 30px;
  
  @media (max-width: 768px) {
    flex-wrap: wrap;
    gap: 15px;
  }
`;

const Badge = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CheckIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  background: #00FFC6;
  color: #0a0e17;
  border-radius: 50%;
  font-size: 0.7rem;
  font-weight: bold;
`;

const BadgeText = styled.span`
  font-size: 0.9rem;
  color: #a0a0a0;
`;

const ImageContainer = styled.div`
  position: relative;
  width: 45%;
  display: flex;
  align-items: center;
  justify-content: center;
  
  @media (max-width: 991px) {
    width: 100%;
    margin-top: 40px;
  }
`;

const WebinarImage = styled.img`
  max-width: 100%;
  height: auto;
  border-radius: 16px;
  object-fit: cover;
  border: 1px solid rgba(0, 255, 198, 0.3);
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(0, 255, 198, 0.1);
  z-index: 2;
`;

const FloatingElements = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;
`;

const FloatingCircle = styled.div`
  position: absolute;
  width: ${props => props.size};
  height: ${props => props.size};
  border-radius: 50%;
  background: rgba(0, 255, 198, 0.1);
  border: 1px solid rgba(0, 255, 198, 0.3);
  top: ${props => props.top};
  left: ${props => props.left};
  animation: ${float} 4s ease-in-out infinite, ${glow} 2s ease-in-out infinite;
  animation-delay: ${props => props.delay};
`;

const FloatingLine = styled.div`
  position: absolute;
  width: ${props => props.width};
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(0, 255, 198, 0.7), transparent);
  top: ${props => props.top};
  left: ${props => props.left};
  transform: rotate(${props => props.rotate});
  animation: ${glow} 3s ease-in-out infinite;
  animation-delay: ${props => props.delay};
`;

export default AIWebinarHero; 